import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import {
  Instagram, Loader2, RefreshCw, Link2, Eye, Users, Bookmark, Share2,
  Heart, MessageCircle, Clock, TrendingUp, Sparkles, PlugZap, Copy, Check,
  ExternalLink, BarChart3, Info, Unplug, Trash2, ArrowRight,
  CalendarDays, Award, Gauge, UserPlus, Flame, MapPin, Globe, PieChart, Wand2,
  Lightbulb, CheckCircle2, Zap, Bookmark as BookmarkIcon, Layers, Fingerprint,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  igStatus, igAuthStart, igSync, igReels, igGrowth, igDisconnect,
  igAnalyze, igGetAnalysis, igSettings, igSaveIdea, igGetSavedIdeas, igDeleteSavedIdea, igTiming,
  igDemographics, igGetReelAnalysis, igAnalyzeReel,
  igBulkAnalyze, igBulkStatus, igAnalyzedIds, igGetSuggestions, igGenerateSuggestions, igPillars, igPatterns,
} from "@/lib/api";
import { toast } from "sonner";

const ACCENT = "#D92D20";

function fmtNum(n) {
  if (n == null) return "—";
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  return String(n);
}

function fmtWatch(ms) {
  if (ms == null) return "—";
  const s = ms / 1000;
  if (s >= 60) return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  return `${s.toFixed(1)}s`;
}

function fmtDate(d) {
  if (!d) return "";
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
  } catch {
    return d;
  }
}

function CopyBtn({ text }) {
  const [c, setC] = useState(false);
  return (
    <button
      data-testid="ig-copy-redirect-btn"
      onClick={() => {
        navigator.clipboard.writeText(text || "");
        setC(true);
        toast.success("Copiato");
        setTimeout(() => setC(false), 1500);
      }}
      className="text-neutral-400 hover:text-white transition-colors"
      title="Copia"
    >
      {c ? <Check size={15} /> : <Copy size={15} />}
    </button>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="border border-[#262626] rounded-lg p-4 bg-[#111111]">
      <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-wide">
        <Icon size={14} style={{ color: color || ACCENT }} />
        {label}
      </div>
      <p className="mt-2 text-2xl font-bold font-display text-white">{value}</p>
    </div>
  );
}

/* ---------- Guida collegamento (quando non connesso) ---------- */
function ConnectPanel({ status, onConnect, connecting }) {
  const configured = status?.configured;
  const redirect = status?.redirect_uri || "";
  const steps = [
    <>Vai su <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" className="text-[#D92D20] underline inline-flex items-center gap-1">developers.facebook.com/apps <ExternalLink size={12} /></a> e accedi col tuo account collegato a Instagram.</>,
    <>Clicca <b>"Crea un'app"</b> → scegli il tipo <b>"Azienda/Business"</b> → dai un nome (es. <i>Studio Massimiliano</i>).</>,
    <>Nella dashboard dell'app aggiungi il prodotto <b>Instagram</b> → <b>"Configura"</b> (API con login di Instagram).</>,
    <>In <b>Impostazioni login business</b>, incolla come <b>"URI di reindirizzamento OAuth valido"</b> esattamente questo indirizzo (copialo qui sotto).</>,
    <>Copia <b>ID app Instagram</b> e <b>Chiave segreta app Instagram</b> e mandameli in chat: li inserisco io nel backend.</>,
    <>Fatto questo, torna qui e clicca <b>"Collega Instagram"</b>: si aprirà Instagram, autorizzi una volta e sei collegato.</>,
  ];
  return (
    <div className="max-w-3xl">
      <div className="border border-[#262626] rounded-xl p-6 bg-[#111111]">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg,#F58529,#DD2A7B,#8134AF)" }}>
            <Instagram size={22} className="text-white" />
          </div>
          <div>
            <h3 className="font-display font-bold text-lg text-white">Collega il tuo Instagram</h3>
            <p className="text-sm text-neutral-500">Dati veri dei tuoi reel: views, copertura, salvataggi, condivisioni, tempo di visione.</p>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {steps.map((s, i) => (
            <div key={i} className="flex gap-3 text-sm text-neutral-300">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#1c1c1c] border border-[#333] flex items-center justify-center text-xs font-bold text-neutral-400">{i + 1}</span>
              <p className="pt-0.5 leading-relaxed">{s}</p>
            </div>
          ))}
        </div>

        <div className="mt-5 border border-[#333] rounded-lg p-3 bg-[#0d0d0d]">
          <p className="text-xs text-neutral-500 mb-1 uppercase tracking-wide">URI di reindirizzamento (copia esatto)</p>
          <div className="flex items-center justify-between gap-3">
            <code className="text-xs text-neutral-300 break-all">{redirect}</code>
            <CopyBtn text={redirect} />
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button
            data-testid="ig-connect-btn"
            disabled={!configured || connecting}
            onClick={onConnect}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm text-white disabled:opacity-40 disabled:cursor-not-allowed transition-transform hover:scale-[1.02]"
            style={{ background: ACCENT }}
          >
            {connecting ? <Loader2 size={16} className="animate-spin" /> : <PlugZap size={16} />}
            Collega Instagram
          </button>
          {!configured && (
            <span className="text-xs text-[#D97706] flex items-center gap-1.5">
              <Info size={13} /> In attesa dei 2 codici (App ID + App Secret) da inserire nel backend.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Grafico crescita ---------- */
function GrowthChart({ data }) {
  const clean = (data || []).filter((d) => d.followers_count != null).map((d) => ({
    date: fmtDate(d.date),
    followers: d.followers_count,
  }));
  if (clean.length < 2) {
    return (
      <div className="border border-[#262626] rounded-xl p-6 bg-[#111111]">
        <h3 className="font-display font-bold text-white flex items-center gap-2"><TrendingUp size={18} style={{ color: ACCENT }} /> Crescita follower</h3>
        <p className="text-sm text-neutral-500 mt-3 leading-relaxed">
          Il grafico si costruisce giorno per giorno da quando hai collegato l'account. Torna tra qualche giorno (o dopo qualche sincronizzazione) per vedere la curva.
        </p>
      </div>
    );
  }
  return (
    <div className="border border-[#262626] rounded-xl p-6 bg-[#111111]" data-testid="ig-growth-chart">
      <h3 className="font-display font-bold text-white flex items-center gap-2 mb-4"><TrendingUp size={18} style={{ color: ACCENT }} /> Crescita follower</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={clean} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
            <XAxis dataKey="date" stroke="#666" fontSize={11} />
            <YAxis stroke="#666" fontSize={11} domain={["auto", "auto"]} />
            <Tooltip contentStyle={{ background: "#0d0d0d", border: "1px solid #333", borderRadius: 8, color: "#fff" }} />
            <Line type="monotone" dataKey="followers" stroke={ACCENT} strokeWidth={2.5} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ---------- Card reel ---------- */
function ReelCard({ r, rank, onClick, analyzed }) {
  return (
    <div onClick={onClick} className="border border-[#262626] rounded-xl overflow-hidden bg-[#111111] group cursor-pointer hover:border-[#3a3a3a] transition-colors" data-testid="ig-reel-card">
      <div className="relative aspect-[9/16] bg-[#0d0d0d] overflow-hidden">
        {analyzed && (
          <span className="absolute bottom-2 right-2 z-10 bg-black/60 rounded-full p-0.5" data-testid="ig-reel-analyzed" title="Analizzato dall'AI">
            <CheckCircle2 size={15} style={{ color: "#22C55E" }} />
          </span>
        )}
        {r.thumbnail_url || r.media_url ? (
          <img src={r.thumbnail_url || r.media_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-700"><Instagram size={40} /></div>
        )}
        <span className="absolute top-2 left-2 text-[0.7rem] font-bold px-2 py-0.5 rounded-full bg-black/70 text-white">#{rank}</span>
        {r.score != null && (
          <span className="absolute top-2 right-2 text-[0.7rem] font-bold px-2 py-0.5 rounded-full bg-black/70 text-white flex items-center gap-1" data-testid="ig-reel-score" title="Performance Score (qualità)">
            <Gauge size={11} style={{ color: "#D97706" }} /> {r.score}
          </span>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/90 to-transparent">
          <div className="flex items-center gap-1 text-white text-sm font-bold"><Eye size={14} /> {fmtNum(r.views)}</div>
        </div>
      </div>
      <div className="p-3">
        <p className="text-xs text-neutral-400 line-clamp-2 min-h-[2rem] leading-relaxed">{r.caption || "—"}</p>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-3 text-xs text-neutral-400">
          <span className="flex items-center gap-1.5"><Heart size={13} className="text-neutral-600" /> {fmtNum(r.likes)}</span>
          <span className="flex items-center gap-1.5"><MessageCircle size={13} className="text-neutral-600" /> {fmtNum(r.comments)}</span>
          <span className="flex items-center gap-1.5"><Bookmark size={13} style={{ color: "#D97706" }} /> {fmtNum(r.saved)}</span>
          <span className="flex items-center gap-1.5"><Share2 size={13} style={{ color: "#16A34A" }} /> {fmtNum(r.shares)}</span>
          <span className="flex items-center gap-1.5"><Users size={13} className="text-neutral-600" /> {fmtNum(r.reach)}</span>
          <span className="flex items-center gap-1.5"><Clock size={13} className="text-neutral-600" /> {fmtWatch(r.avg_watch_time)}</span>
        </div>
        {r.engagement_rate != null && (
          <p className="mt-2 text-[0.7rem] text-neutral-500">Engagement: <span className="text-white font-semibold">{r.engagement_rate}%</span></p>
        )}
        {r.followers_gained_est != null && (
          <p className="mt-1.5 text-[0.7rem] flex items-center gap-1.5" style={{ color: "#16A34A" }} data-testid="ig-reel-followers" title="Stima dai follower guadagnati nel giorno di pubblicazione">
            <UserPlus size={12} /> {r.followers_gained_est >= 0 ? "+" : ""}{fmtNum(r.followers_gained_est)} follower <span className="text-neutral-600">{r.followers_gained_ambiguous ? "(stima giorno)" : "(stima)"}</span>
          </p>
        )}
      </div>
    </div>
  );
}

/* ---------- Pannello AI ---------- */
function TemaCard({ t, onSaveIdea }) {
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  return (
    <div className="border border-[#262626] rounded-lg p-3 bg-[#0d0d0d]">
      <div className="flex items-start justify-between gap-3">
        <p className="text-white font-semibold">{t.tema}</p>
        <button
          data-testid="ig-save-idea-btn"
          disabled={saved || saving}
          onClick={async () => {
            setSaving(true);
            const ok = await onSaveIdea({ tema: t.tema, angolo: t.angolo, hook: t.hook, perche: t.perche });
            if (ok) setSaved(true);
            setSaving(false);
          }}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-70"
          style={saved ? { borderColor: "#16A34A", color: "#16A34A" } : { borderColor: "#333", color: "#fff" }}
        >
          {saved ? <><Check size={13} /> Salvata</> : saving ? <Loader2 size={13} className="animate-spin" /> : <><Bookmark size={13} /> Salva</>}
        </button>
      </div>
      {t.hook && (
        <div className="mt-2">
          <span className="text-[0.6rem] uppercase tracking-wide text-neutral-500">Hook pronto</span>
          <p className="text-sm text-neutral-200 leading-relaxed">«{t.hook}»</p>
        </div>
      )}
      {t.angolo && (
        <div className="mt-2">
          <span className="text-[0.6rem] uppercase tracking-wide text-neutral-500">Angolo</span>
          <p className="text-xs text-neutral-300 leading-relaxed">{t.angolo}</p>
        </div>
      )}
      {t.perche && <p className="text-neutral-500 text-xs mt-2 leading-relaxed">{t.perche}</p>}
    </div>
  );
}

function AiPanel({ analysis, analyzing, onAnalyze, periodLabel, onSaveIdea }) {
  return (
    <div className="border border-[#262626] rounded-xl p-6 bg-[#111111]" data-testid="ig-ai-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-display font-bold text-white flex items-center gap-2">
          <Sparkles size={18} style={{ color: "#D97706" }} /> Cosa dice l'AI sui tuoi dati
          <span className="text-[0.65rem] font-semibold px-2 py-0.5 rounded-full bg-[#1c1c1c] border border-[#333] text-neutral-400 normal-case">{periodLabel}</span>
        </h3>
        <button
          data-testid="ig-analyze-btn"
          onClick={onAnalyze}
          disabled={analyzing}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50 transition-transform hover:scale-[1.02]"
          style={{ background: "#D97706" }}
        >
          {analyzing ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
          {analysis ? "Rianalizza" : "Analizza i reel"}
        </button>
      </div>

      {!analysis && !analyzing && (
        <p className="text-sm text-neutral-500 mt-4 leading-relaxed">
          Fai analizzare all'AI i tuoi reel di <b className="text-neutral-300">{periodLabel}</b>: ti dice quali pattern (hook, temi, formati) funzionano davvero guardando salvataggi, condivisioni e tempo di visione, e ti propone i prossimi temi da girare.
        </p>
      )}

      {analysis && (
        <div className="mt-5 space-y-5 text-sm">
          {analysis.sintesi && <p className="text-neutral-200 leading-relaxed border-l-2 pl-3" style={{ borderColor: "#D97706" }}>{analysis.sintesi}</p>}

          {Array.isArray(analysis.cosa_funziona) && analysis.cosa_funziona.length > 0 && (
            <div>
              <p className="font-semibold text-white mb-2 flex items-center gap-2"><TrendingUp size={15} className="text-[#16A34A]" /> Cosa funziona</p>
              <ul className="space-y-1.5">
                {analysis.cosa_funziona.map((x, i) => <li key={i} className="text-neutral-300 flex gap-2"><span className="text-[#16A34A]">•</span>{x}</li>)}
              </ul>
            </div>
          )}

          {Array.isArray(analysis.da_evitare) && analysis.da_evitare.length > 0 && (
            <div>
              <p className="font-semibold text-white mb-2 flex items-center gap-2"><Info size={15} className="text-[#D92D20]" /> Da evitare</p>
              <ul className="space-y-1.5">
                {analysis.da_evitare.map((x, i) => <li key={i} className="text-neutral-300 flex gap-2"><span className="text-[#D92D20]">•</span>{x}</li>)}
              </ul>
            </div>
          )}

          {analysis.cosa_salva_condivide_il_pubblico && (
            <div className="border border-[#262626] rounded-lg p-3 bg-[#0d0d0d]">
              <p className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Cosa salva/condivide il pubblico</p>
              <p className="text-neutral-200 leading-relaxed">{analysis.cosa_salva_condivide_il_pubblico}</p>
            </div>
          )}

          {Array.isArray(analysis.prossimi_temi) && analysis.prossimi_temi.length > 0 && (
            <div>
              <p className="font-semibold text-white mb-2 flex items-center gap-2"><Sparkles size={15} style={{ color: "#D97706" }} /> Prossimi temi consigliati</p>
              <div className="space-y-2">
                {analysis.prossimi_temi.map((t, i) => <TemaCard key={i} t={t} onSaveIdea={onSaveIdea} />)}
              </div>
            </div>
          )}
          {analysis.reels_analizzati != null && (
            <p className="text-[0.7rem] text-neutral-600">Basato su {analysis.reels_analizzati} reel.</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- Timing: giorno + fascia oraria migliore ---------- */
const DAY_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const SLOT_ROWS = [
  { key: "mattina_presto", label: "5-8" },
  { key: "mattina", label: "8-12" },
  { key: "pranzo", label: "12-15" },
  { key: "pomeriggio", label: "15-18" },
  { key: "sera", label: "18-22" },
  { key: "notte", label: "22-5" },
];
const CONF_LABEL = { alta: "affidabilità alta", media: "affidabilità media", bassa: "pochi dati" };

function heatColor(score, max) {
  if (!score || !max) return "transparent";
  const a = 0.1 + 0.8 * Math.min(score / max, 1);
  return `rgba(217,45,32,${a.toFixed(2)})`;
}

function TimingBar({ label, sub, value, max }) {
  const pct = max ? Math.max(6, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-3" data-testid="ig-timing-bar">
      <div className="w-16 shrink-0 text-right">
        <p className="text-xs text-neutral-300 leading-none">{label}</p>
        {sub != null && <p className="text-[0.6rem] text-neutral-600 mt-0.5">{sub} reel</p>}
      </div>
      <div className="flex-1 h-5 rounded bg-[#0d0d0d] border border-[#262626] overflow-hidden relative">
        <div className="h-full rounded-l transition-all duration-500" style={{ width: `${pct}%`, background: ACCENT, opacity: 0.8 }} />
        <span className="absolute inset-y-0 right-2 flex items-center text-[0.65rem] font-semibold text-neutral-300">{value}</span>
      </div>
    </div>
  );
}

function BestCard({ icon: Icon, kicker, title, sub, score, confidence }) {
  return (
    <div className="border border-[#262626] rounded-xl p-5 bg-[#0d0d0d] flex-1 min-w-[200px]" data-testid="ig-best-card">
      <div className="flex items-center gap-2 text-[0.65rem] uppercase tracking-wide text-neutral-500">
        <Icon size={14} style={{ color: "#D97706" }} /> {kicker}
      </div>
      <p className="mt-2 text-2xl font-display font-extrabold text-white leading-none">{title}</p>
      {sub && <p className="text-xs text-neutral-400 mt-1">{sub}</p>}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <span className="text-[0.7rem] font-semibold px-2 py-0.5 rounded-full text-white" style={{ background: ACCENT }}>Score {score}/100</span>
        {confidence && <span className="text-[0.65rem] text-neutral-500">{CONF_LABEL[confidence] || confidence}</span>}
      </div>
    </div>
  );
}

function TimingPanel({ timing }) {
  if (!timing || !timing.total_reels) {
    return (
      <div className="border border-[#262626] rounded-xl p-6 bg-[#111111]" data-testid="ig-timing-panel">
        <h3 className="font-display font-bold text-white flex items-center gap-2"><CalendarDays size={18} style={{ color: "#D97706" }} /> Quando pubblicare</h3>
        <p className="text-sm text-neutral-500 mt-3">Servono più reel nel periodo selezionato. Prova a scegliere <b className="text-neutral-300">Tutto</b> come periodo per un quadro più solido.</p>
      </div>
    );
  }
  const maxDay = Math.max(...timing.by_day.map((d) => d.avg_score), 1);
  const maxSlot = Math.max(...timing.by_slot.map((s) => s.avg_score), 1);
  const cellMax = Math.max(...timing.heatmap.map((c) => c.avg_score), 1);
  const cellMap = {};
  timing.heatmap.forEach((c) => { cellMap[`${c.day}-${c.slot}`] = c; });

  return (
    <div className="border border-[#262626] rounded-xl p-6 bg-[#111111] space-y-6" data-testid="ig-timing-panel">
      <div>
        <h3 className="font-display font-bold text-white flex items-center gap-2"><CalendarDays size={18} style={{ color: "#D97706" }} /> Quando pubblicare — i tuoi momenti migliori</h3>
        <p className="text-xs text-neutral-500 mt-1">In base al <b className="text-neutral-400">Performance Score</b> (qualità: salvataggi, condivisioni, tempo di visione), ora italiana. Basato su {timing.total_reels} reel.</p>
      </div>

      {/* Consiglio operativo pronto */}
      {timing.best_combo && (
        <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: "rgba(217,45,32,0.10)", border: "1px solid rgba(217,45,32,0.35)" }} data-testid="ig-timing-reco">
          <Flame size={20} style={{ color: ACCENT }} className="mt-0.5 shrink-0" />
          <div>
            <p className="text-[0.65rem] uppercase tracking-wide text-neutral-400">Consiglio</p>
            <p className="text-white font-semibold leading-snug">
              Il tuo momento d'oro è <span style={{ color: "#fff" }}>{timing.best_combo.day_label} nella fascia {timing.best_combo.slot_label} ({timing.best_combo.range})</span> — score medio {timing.best_combo.avg_score}/100 su {timing.best_combo.count} reel.
            </p>
          </div>
        </div>
      )}

      <div className="flex gap-3 flex-wrap">
        {timing.best_day && <BestCard icon={CalendarDays} kicker="Giorno migliore" title={timing.best_day.label} sub={`${timing.best_day.count} reel`} score={timing.best_day.avg_score} confidence={timing.best_day.confidence} />}
        {timing.best_slot && <BestCard icon={Clock} kicker="Fascia oraria migliore" title={timing.best_slot.label} sub={timing.best_slot.range} score={timing.best_slot.avg_score} confidence={timing.best_slot.confidence} />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <p className="text-xs uppercase tracking-wide text-neutral-500 mb-3">Per giorno della settimana</p>
          <div className="space-y-2">
            {timing.by_day.map((d) => <TimingBar key={d.day} label={DAY_SHORT[d.day]} sub={d.count} value={d.avg_score} max={maxDay} />)}
          </div>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-neutral-500 mb-3">Per fascia oraria</p>
          <div className="space-y-2">
            {timing.by_slot.map((s) => <TimingBar key={s.key} label={s.range} sub={s.count} value={s.avg_score} max={maxSlot} />)}
          </div>
        </div>
      </div>

      {/* Heatmap giorno × fascia */}
      <div>
        <p className="text-xs uppercase tracking-wide text-neutral-500 mb-3">Mappa giorno × fascia (colore = score medio)</p>
        <div className="overflow-x-auto">
          <table className="border-separate" style={{ borderSpacing: 4 }} data-testid="ig-heatmap">
            <thead>
              <tr>
                <th className="w-14"></th>
                {DAY_SHORT.map((d, i) => <th key={i} className="text-[0.65rem] font-semibold text-neutral-500 pb-1 w-12 text-center">{d}</th>)}
              </tr>
            </thead>
            <tbody>
              {SLOT_ROWS.map((row) => (
                <tr key={row.key}>
                  <td className="text-[0.65rem] text-neutral-500 text-right pr-1 whitespace-nowrap">{row.label}</td>
                  {DAY_SHORT.map((_, di) => {
                    const c = cellMap[`${di}-${row.key}`];
                    return (
                      <td
                        key={di}
                        className="h-9 w-12 rounded text-center align-middle border border-[#1f1f1f]"
                        style={{ background: c ? heatColor(c.avg_score, cellMax) : "rgba(255,255,255,0.02)" }}
                        title={c ? `${row.label}: score ${c.avg_score}/100 su ${c.count} reel` : "nessun reel"}
                      >
                        {c && <span className="text-[0.6rem] font-semibold text-white">{c.avg_score}</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {timing.note && (
        <p className="text-xs text-[#D97706] flex items-start gap-2"><Info size={13} className="mt-0.5 shrink-0" /> {timing.note}</p>
      )}
      <p className="text-[0.7rem] text-neutral-600 leading-relaxed">
        Nota: calcolato sui <b>tuoi</b> reel passati (cosa ha funzionato davvero per te). I giorni/fasce con pochi reel sono ammorbiditi verso la media, così i numeri non ti ingannano.
      </p>
    </div>
  );
}

/* ---------- Demografica pubblico ---------- */
function DemoBar({ label, value, total, color }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3" data-testid="ig-demo-bar">
      <div className="w-24 shrink-0 text-right text-xs text-neutral-300">{label}</div>
      <div className="flex-1 h-5 rounded bg-[#0d0d0d] border border-[#262626] overflow-hidden relative">
        <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: color || ACCENT, opacity: 0.85 }} />
        <span className="absolute inset-y-0 right-2 flex items-center text-[0.65rem] text-neutral-300">{pct}% · {fmtNum(value)}</span>
      </div>
    </div>
  );
}

function DemographicsPanel({ demo }) {
  if (!demo) return null;
  if (!demo.available) {
    return (
      <div className="border border-[#262626] rounded-xl p-6 bg-[#111111]" data-testid="ig-demo-panel">
        <h3 className="font-display font-bold text-white flex items-center gap-2"><PieChart size={18} style={{ color: "#D97706" }} /> Chi ti segue</h3>
        <p className="text-sm text-neutral-500 mt-3">I dati demografici non sono ancora disponibili (servono ~100 follower e qualche giorno di dati). Riprova dopo la prossima sincronizzazione.</p>
      </div>
    );
  }
  const genderTotal = (demo.gender || []).reduce((a, g) => a + g.value, 0);
  const ageTotal = (demo.age || []).reduce((a, g) => a + g.value, 0);
  const maxCity = Math.max(...(demo.city || []).map((c) => c.value), 1);
  const maxCountry = Math.max(...(demo.country || []).map((c) => c.value), 1);
  const GENDER_COLOR = { F: "#DB2777", M: "#2563EB", U: "#6B7280" };
  const donne = (demo.gender || []).find((g) => g.key === "F");
  const donnePct = genderTotal && donne ? Math.round((donne.value / genderTotal) * 100) : null;
  return (
    <div className="border border-[#262626] rounded-xl p-6 bg-[#111111] space-y-6" data-testid="ig-demo-panel">
      <div>
        <h3 className="font-display font-bold text-white flex items-center gap-2"><PieChart size={18} style={{ color: "#D97706" }} /> Chi ti segue</h3>
        <p className="text-xs text-neutral-500 mt-1">
          Su {fmtNum(demo.followers_count)} follower — dati veri dell'algoritmo.
          {donnePct != null && <> Il tuo pubblico è <b className="text-neutral-300">{donnePct}% donne</b>: in linea con la tua strategia.</>}
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <p className="text-xs uppercase tracking-wide text-neutral-500 mb-3">Genere</p>
          <div className="space-y-2">
            {(demo.gender || []).map((g) => <DemoBar key={g.key} label={g.label} value={g.value} total={genderTotal} color={GENDER_COLOR[g.key]} />)}
          </div>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-neutral-500 mb-3">Fasce d'età</p>
          <div className="space-y-2">
            {(demo.age || []).map((a) => <DemoBar key={a.label} label={a.label} value={a.value} total={ageTotal} color="#D97706" />)}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <p className="text-xs uppercase tracking-wide text-neutral-500 mb-3 flex items-center gap-1.5"><MapPin size={13} /> Città principali</p>
          <div className="space-y-1.5">
            {(demo.city || []).map((c, i) => (
              <div key={i} className="flex items-center gap-2 text-xs" data-testid="ig-demo-city">
                <span className="w-40 truncate text-neutral-300">{c.label}</span>
                <div className="flex-1 h-2 rounded-full bg-[#0d0d0d] overflow-hidden"><div className="h-full" style={{ width: `${Math.round((c.value / maxCity) * 100)}%`, background: ACCENT, opacity: 0.7 }} /></div>
                <span className="text-neutral-500 w-8 text-right">{fmtNum(c.value)}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-neutral-500 mb-3 flex items-center gap-1.5"><Globe size={13} /> Paesi principali</p>
          <div className="space-y-1.5">
            {(demo.country || []).map((c, i) => (
              <div key={i} className="flex items-center gap-2 text-xs" data-testid="ig-demo-country">
                <span className="w-40 truncate text-neutral-300">{c.label}</span>
                <div className="flex-1 h-2 rounded-full bg-[#0d0d0d] overflow-hidden"><div className="h-full" style={{ width: `${Math.round((c.value / maxCountry) * 100)}%`, background: "#D97706", opacity: 0.7 }} /></div>
                <span className="text-neutral-500 w-8 text-right">{fmtNum(c.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Dettaglio reel (modal + analisi AI) ---------- */
function MetricCell({ icon: Icon, label, value, color }) {
  return (
    <div className="border border-[#262626] rounded-lg p-3 bg-[#0d0d0d] text-center">
      <Icon size={15} className="mx-auto mb-1" style={{ color: color || "#9CA3AF" }} />
      <p className="text-base font-bold text-white leading-none">{value}</p>
      <p className="text-[0.6rem] uppercase tracking-wide text-neutral-500 mt-1">{label}</p>
    </div>
  );
}

function ReelDetail({ reel, open, onClose, analysis, analyzing, onAnalyze, onReplicate }) {
  const a = analysis;
  const rows = a ? [
    ["Hook", a.hook], ["Topic", a.topic], ["Formato", a.formato], ["Tono", a.tono],
    ["Content pillar", a.content_pillar], ["Tipo (funnel)", a.tipo_funnel],
    ["Awareness", a.awareness], ["CTA", a.cta],
  ] : [];
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl bg-[#111111] border-[#262626] text-neutral-100 max-h-[88vh] overflow-y-auto" data-testid="ig-reel-detail">
        <DialogHeader>
          <DialogTitle className="text-white font-display flex items-center gap-2"><BarChart3 size={18} style={{ color: ACCENT }} /> Dettaglio reel</DialogTitle>
        </DialogHeader>
        {reel && (
          <>
            <div className="flex gap-4">
              <div className="w-28 shrink-0 aspect-[9/16] rounded-lg overflow-hidden bg-[#0d0d0d]">
                {reel.thumbnail_url || reel.media_url ? <img src={reel.thumbnail_url || reel.media_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-neutral-700"><Instagram size={28} /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-neutral-300 line-clamp-4 leading-relaxed">{reel.caption || "—"}</p>
                <div className="flex items-center gap-3 mt-3 flex-wrap">
                  {reel.score != null && <span className="text-xs font-bold px-2.5 py-1 rounded-full text-white flex items-center gap-1" style={{ background: ACCENT }}><Gauge size={12} /> Score {reel.score}/100</span>}
                  {reel.followers_gained_est != null && <span className="text-xs flex items-center gap-1" style={{ color: "#16A34A" }}><UserPlus size={12} /> {reel.followers_gained_est >= 0 ? "+" : ""}{fmtNum(reel.followers_gained_est)}</span>}
                  {reel.permalink && <a href={reel.permalink} target="_blank" rel="noreferrer" className="text-xs text-neutral-400 hover:text-white flex items-center gap-1" data-testid="ig-reel-open">Apri su Instagram <ExternalLink size={12} /></a>}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-2">
              <MetricCell icon={Eye} label="Views" value={fmtNum(reel.views)} color="#16A34A" />
              <MetricCell icon={Users} label="Reach" value={fmtNum(reel.reach)} />
              <MetricCell icon={Heart} label="Like" value={fmtNum(reel.likes)} />
              <MetricCell icon={MessageCircle} label="Commenti" value={fmtNum(reel.comments)} />
              <MetricCell icon={Bookmark} label="Salvat." value={fmtNum(reel.saved)} color="#D97706" />
              <MetricCell icon={Share2} label="Condiv." value={fmtNum(reel.shares)} color="#16A34A" />
            </div>

            <div className="border-t border-[#262626] mt-4 pt-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h4 className="font-semibold text-white flex items-center gap-2"><Sparkles size={16} style={{ color: "#D97706" }} /> Analisi AI</h4>
                <button data-testid="ig-reel-analyze-btn" onClick={onAnalyze} disabled={analyzing} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50" style={{ background: "#D97706" }}>
                  {analyzing ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} {a ? "Rianalizza" : "Analizza questo reel"}
                </button>
              </div>
              {!a && !analyzing && <p className="text-sm text-neutral-500 mt-3">Scomponi questo reel: hook, formato, tono, e come replicarne il risultato su un altro tema (in linea con la tua North Star).</p>}
              {analyzing && !a && <p className="text-sm text-neutral-500 mt-3 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Sto analizzando…</p>}
              {a && (
                <div className="mt-4 space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    {rows.map(([k, v]) => v ? (
                      <div key={k}><span className="text-[0.6rem] uppercase tracking-wide text-neutral-500 block">{k}</span><span className="text-neutral-200">{v}</span></div>
                    ) : null)}
                  </div>
                  {a.perche_ha_funzionato && <div className="border border-[#262626] rounded-lg p-3 bg-[#0d0d0d]"><span className="text-[0.6rem] uppercase tracking-wide text-neutral-500 block mb-1">Perché ha funzionato</span><p className="text-neutral-200 leading-relaxed">{a.perche_ha_funzionato}</p></div>}
                  {a.come_replicare && <div className="border rounded-lg p-3" style={{ background: "rgba(217,45,32,0.08)", borderColor: "rgba(217,45,32,0.3)" }}><span className="text-[0.6rem] uppercase tracking-wide text-neutral-400 block mb-1">Come replicare</span><p className="text-neutral-100 leading-relaxed">{a.come_replicare}</p></div>}
                  <button data-testid="ig-reel-replicate-btn" onClick={() => onReplicate(a, reel)} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-transform hover:scale-[1.02]" style={{ background: ACCENT }}>
                    <Wand2 size={15} /> Replica questo risultato → Generatore
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Suggerimenti editoriali (idee pronte dai dati) ---------- */
function SuggestionCard({ s, onUse, onSave }) {
  const [saved, setSaved] = useState(false);
  return (
    <div className="border border-[#262626] rounded-lg p-4 bg-[#0d0d0d] flex flex-col" data-testid="ig-suggestion-card">
      <p className="text-white font-semibold leading-snug">{s.hook}</p>
      {s.angolo && <p className="text-xs text-neutral-400 leading-relaxed mt-2">{s.angolo}</p>}
      <div className="flex flex-wrap gap-1.5 mt-3">
        {s.content_pillar && <span className="text-[0.6rem] px-2 py-0.5 rounded-full" style={{ background: "rgba(217,45,32,0.15)", color: "#F87171" }}>{s.content_pillar}</span>}
        {s.tipo_funnel && <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-[#1f1f1f] text-neutral-300">{s.tipo_funnel}</span>}
        {s.formato && <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-[#1f1f1f] text-neutral-400">{s.formato}</span>}
      </div>
      {s.quando_pubblicare && <p className="text-[0.7rem] text-neutral-500 mt-2 flex items-center gap-1.5"><CalendarDays size={12} /> {s.quando_pubblicare}</p>}
      {s.perche_funziona && <p className="text-[0.7rem] text-neutral-600 mt-1 leading-relaxed">{s.perche_funziona}</p>}
      <div className="flex items-center gap-3 mt-3 pt-3 border-t border-[#1f1f1f]">
        <button data-testid="ig-suggestion-use" onClick={() => onUse(s)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#D92D20] hover:gap-2.5 transition-all">Scrivi lo script <ArrowRight size={14} /></button>
        <button data-testid="ig-suggestion-save" disabled={saved} onClick={async () => { const ok = await onSave(s); if (ok) setSaved(true); }} className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-white disabled:opacity-50 ml-auto">
          {saved ? <><Check size={13} /> Salvata</> : <><BookmarkIcon size={13} /> Salva</>}
        </button>
      </div>
    </div>
  );
}

function SuggestionsPanel({ suggestions, generatedAt, generating, onGenerate, onUse, onSave }) {
  return (
    <div className="border border-[#262626] rounded-xl p-6 bg-[#111111]" data-testid="ig-suggestions-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-white flex items-center gap-2"><Lightbulb size={18} style={{ color: "#D97706" }} /> Suggerimenti editoriali</h3>
          <p className="text-xs text-neutral-500 mt-1">6 idee pronte, costruite dai tuoi dati veri: pubblico, timing e reel migliori.</p>
        </div>
        <button data-testid="ig-suggestions-generate" onClick={onGenerate} disabled={generating} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ background: ACCENT }}>
          {generating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} {suggestions.length ? "Rigenera" : "Genera suggerimenti"}
        </button>
      </div>
      {generating && !suggestions.length && <p className="text-sm text-neutral-500 mt-4 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Sto pensando alle idee migliori per te…</p>}
      {!generating && !suggestions.length && <p className="text-sm text-neutral-500 mt-4">Premi <b className="text-neutral-300">Genera suggerimenti</b> per ricevere 6 idee di reel su misura.</p>}
      {suggestions.length > 0 && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-4">
            {suggestions.map((s, i) => <SuggestionCard key={i} s={s} onUse={onUse} onSave={onSave} />)}
          </div>
          {generatedAt && <p className="text-[0.65rem] text-neutral-600 mt-3">Generati il {new Date(generatedAt).toLocaleString("it-IT")}</p>}
        </>
      )}
    </div>
  );
}

/* ---------- Confronto pillar / funnel (cosa rende di più) ---------- */
function PerfBar({ label, score, count, views, ss, max, top }) {
  const pct = max ? Math.max(8, Math.round((score / max) * 100)) : 0;
  return (
    <div data-testid="ig-pillar-row">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-white font-semibold flex items-center gap-1.5">
          {top && <Award size={13} style={{ color: "#D97706" }} />}{label}
        </span>
        <span className="text-xs text-neutral-500">{count} reel</span>
      </div>
      <div className="h-6 rounded bg-[#0d0d0d] border border-[#262626] overflow-hidden relative">
        <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: top ? ACCENT : "#374151", opacity: 0.85 }} />
        <span className="absolute inset-y-0 right-2 flex items-center text-[0.65rem] font-semibold text-white">{score}/100</span>
      </div>
      <p className="text-[0.65rem] text-neutral-600 mt-1">⌀ {fmtNum(views)} views · {fmtNum(ss)} salv+cond</p>
    </div>
  );
}

function PillarComparePanel({ data }) {
  if (!data || !data.pillars || data.pillars.length === 0) {
    return (
      <div className="border border-[#262626] rounded-xl p-6 bg-[#111111]" data-testid="ig-pillars-panel">
        <h3 className="font-display font-bold text-white flex items-center gap-2"><Layers size={18} style={{ color: "#D97706" }} /> Cosa rende di più</h3>
        <p className="text-sm text-neutral-500 mt-3">Analizza qualche reel con l'AI (per assegnargli pillar e funnel) e qui vedrai quale tipo di contenuto rende di più.</p>
      </div>
    );
  }
  const maxP = Math.max(...data.pillars.map((p) => p.avg_score), 1);
  const maxF = Math.max(...data.funnels.map((f) => f.avg_score), 1);
  const best = data.best_pillar;
  return (
    <div className="border border-[#262626] rounded-xl p-6 bg-[#111111] space-y-6" data-testid="ig-pillars-panel">
      <div>
        <h3 className="font-display font-bold text-white flex items-center gap-2"><Layers size={18} style={{ color: "#D97706" }} /> Cosa rende di più</h3>
        <p className="text-xs text-neutral-500 mt-1">Confronto per <b className="text-neutral-400">pilastro</b> e <b className="text-neutral-400">funnel</b> sul Performance Score (qualità). Su {data.analyzed} reel analizzati.</p>
      </div>

      {best && (
        <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: "rgba(217,45,32,0.10)", border: "1px solid rgba(217,45,32,0.35)" }} data-testid="ig-pillars-reco">
          <Award size={20} style={{ color: ACCENT }} className="mt-0.5 shrink-0" />
          <div>
            <p className="text-[0.65rem] uppercase tracking-wide text-neutral-400">Il pilastro che ti rende di più</p>
            <p className="text-white font-semibold leading-snug">
              <span style={{ color: "#fff" }}>{best.label}</span> — score medio {best.avg_score}/100, in media {fmtNum(best.avg_views)} views e {fmtNum(best.avg_saves_shares)} tra salvataggi e condivisioni per reel ({best.count} reel).
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <p className="text-xs uppercase tracking-wide text-neutral-500 mb-3">Per pilastro</p>
          <div className="space-y-4">
            {data.pillars.map((p, i) => <PerfBar key={p.label} label={p.label} score={p.avg_score} count={p.count} views={p.avg_views} ss={p.avg_saves_shares} max={maxP} top={i === 0} />)}
          </div>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-neutral-500 mb-3">Per funnel</p>
          <div className="space-y-4">
            {data.funnels.map((f, i) => <PerfBar key={f.label} label={f.label} score={f.avg_score} count={f.count} views={f.avg_views} ss={f.avg_saves_shares} max={maxF} top={i === 0} />)}
          </div>
        </div>
      </div>
    </div>
  );
}

const PATTERN_ORDER = ["hook", "formato", "tono", "cta", "awareness"];

function PatternColumn({ dim, group }) {
  const rows = (group?.rows || []).slice(0, 6);
  const max = Math.max(...rows.map((r) => r.avg_score), 1);
  const ex = rows[0]?.example;
  return (
    <div data-testid={`ig-pattern-${dim}`}>
      <p className="text-xs uppercase tracking-wide text-neutral-500 mb-3">{group?.label}</p>
      {rows.length === 0 ? (
        <p className="text-xs text-neutral-600">Nessun dato.</p>
      ) : (
        <div className="space-y-4">
          {rows.map((r, i) => <PerfBar key={r.label} label={r.label} score={r.avg_score} count={r.count} views={r.avg_views} ss={r.avg_saves_shares} max={max} top={i === 0} />)}
        </div>
      )}
      {rows[0] && rows[0].count < 5 && (
        <p className="text-[0.65rem] text-amber-500/80 mt-2" data-testid={`ig-pattern-${dim}-lowdata`}>Pochi reel in testa ({rows[0].count}): leggi con cautela.</p>
      )}
      {ex?.hook && (
        <p className="text-[0.7rem] text-neutral-500 mt-3 italic leading-snug" data-testid={`ig-pattern-${dim}-example`}>
          Es. migliore: «{ex.hook}»{ex.permalink && <> · <a href={ex.permalink} target="_blank" rel="noreferrer" className="underline hover:text-white not-italic">apri</a></>}
        </p>
      )}
    </div>
  );
}

function PatternsPanel({ data }) {
  const header = (
    <h3 className="font-display font-bold text-white flex items-center gap-2"><Fingerprint size={18} style={{ color: "#D97706" }} /> Pattern vincenti</h3>
  );
  if (!data || !data.analyzed) {
    return (
      <div className="border border-[#262626] rounded-xl p-6 bg-[#111111]" data-testid="ig-patterns-panel">
        {header}
        <p className="text-sm text-neutral-500 mt-3">Analizza qualche reel con l'AI e qui vedrai quale tipo di hook, formato, tono e CTA rende di più.</p>
      </div>
    );
  }
  const b = data.best || {};
  const recipe = [b.hook && `hook «${b.hook}»`, b.formato && `formato «${b.formato}»`, b.tono && `tono «${b.tono}»`, b.cta && `CTA «${b.cta}»`].filter(Boolean);
  return (
    <div className="border border-[#262626] rounded-xl p-6 bg-[#111111] space-y-6" data-testid="ig-patterns-panel">
      <div>
        {header}
        <p className="text-xs text-neutral-500 mt-1">Come è costruito un reel che funziona: <b className="text-neutral-400">hook, formato, tono, CTA</b> classificati dalle analisi AI e incrociati con il Performance Score. Su {data.analyzed} reel analizzati (di {data.total_reels} nel periodo).</p>
      </div>

      {recipe.length > 0 && (
        <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: "rgba(217,119,6,0.10)", border: "1px solid rgba(217,119,6,0.35)" }} data-testid="ig-patterns-recipe">
          <Wand2 size={20} style={{ color: "#D97706" }} className="mt-0.5 shrink-0" />
          <div>
            <p className="text-[0.65rem] uppercase tracking-wide text-neutral-400">La tua ricetta vincente</p>
            <p className="text-white font-semibold leading-snug">{recipe.join(" · ")}.</p>
            <p className="text-xs text-neutral-500 mt-1">Ogni classifica è "smorzata" verso la media: i gruppi con pochi reel non saltano in testa per un colpo di fortuna.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {PATTERN_ORDER.map((dim) => <PatternColumn key={dim} dim={dim} group={data.groups?.[dim]} />)}
      </div>
    </div>
  );
}

const SORTS = [
  { id: "score", label: "Score" },
  { id: "views", label: "Views" },
  { id: "reach", label: "Copertura" },
  { id: "saved", label: "Salvataggi" },
  { id: "shares", label: "Condivisioni" },
  { id: "engagement", label: "Engagement" },
  { id: "recent", label: "Recenti" },
];

const PILLARS = ["Tutti", "Sistema", "Tabù di coppia", "Connessione"];
const FUNNELS = ["Tutti", "Attenzione", "Educazione", "Autorevolezza", "Conversione"];
const filterMatch = (val, f) => f === "Tutti" || (!!val && val.toLowerCase().includes(f.toLowerCase()));

function FilterChip({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`ig-filter-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`px-2.5 py-1 rounded-full text-[0.7rem] font-semibold transition-colors ${active ? "text-white" : "text-neutral-500 hover:text-neutral-300 border border-[#262626]"}`}
      style={active ? { background: ACCENT } : {}}
    >
      {label}
    </button>
  );
}

/* ---------- Idee salvate dall'AI (restano qui in Andamento) ---------- */
function SavedIdeasPanel({ ideas, onDelete, onUseIdea }) {
  return (
    <div className="border border-[#262626] rounded-xl p-6 bg-[#111111]" data-testid="ig-saved-ideas-panel">
      <h3 className="font-display font-bold text-white flex items-center gap-2">
        <Bookmark size={18} style={{ color: "#D97706" }} fill="#D97706" /> Le tue idee salvate
        {ideas.length > 0 && <span className="text-neutral-500 font-normal text-sm">({ideas.length})</span>}
      </h3>
      <p className="text-sm text-neutral-500 mt-1 mb-4">Le idee che hai messo da parte dall'analisi AI. Restano qui in Andamento, dove nascono.</p>

      {ideas.length === 0 ? (
        <div className="border border-dashed border-[#262626] rounded-lg py-8 text-center text-neutral-500 text-sm">
          Nessuna idea salvata ancora. Premi <b className="text-white">Salva</b> su un tema proposto dall'AI qui sopra.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {ideas.map((idea) => (
            <div key={idea.id} className="border border-[#262626] rounded-lg p-4 bg-[#0d0d0d] flex flex-col" data-testid="ig-saved-idea-card">
              <div className="flex items-start justify-between gap-3 mb-2">
                <p className="text-white font-semibold leading-snug">{idea.hook_fissato}</p>
                <button
                  data-testid={`ig-saved-idea-delete-${idea.id}`}
                  onClick={() => onDelete(idea.id)}
                  className="text-neutral-600 hover:text-[#D92D20] transition-colors flex-shrink-0"
                  title="Rimuovi"
                >
                  <Trash2 size={15} />
                </button>
              </div>
              {idea.tema && <p className="text-[0.65rem] uppercase tracking-wide text-neutral-500 mb-1">{idea.tema}</p>}
              {idea.angolo && <p className="text-xs text-neutral-300 leading-relaxed mb-2">{idea.angolo}</p>}
              {idea.perche_funziona && <p className="text-[0.7rem] text-neutral-500 leading-relaxed mb-3">{idea.perche_funziona}</p>}
              {onUseIdea && (
                <button
                  data-testid="ig-saved-idea-use"
                  onClick={() => onUseIdea(idea)}
                  className="mt-auto self-start inline-flex items-center gap-1.5 text-sm font-semibold text-[#D92D20] hover:gap-2.5 transition-all"
                >
                  Scrivi lo script <ArrowRight size={15} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const PERIODS = [
  { key: "since", label: "Da inizio lavoro" },
  { key: "m1", months: 1, label: "Ultimo mese" },
  { key: "m3", months: 3, label: "Ultimi 3 mesi" },
  { key: "all", months: 0, label: "Tutto" },
];

export default function InstagramDashboard({ onUseIdea }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [reels, setReels] = useState([]);
  const [growth, setGrowth] = useState([]);
  const [sort, setSort] = useState("views");
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [periodKey, setPeriodKey] = useState("since");
  const [startDate, setStartDate] = useState(null);
  const [savedIdeas, setSavedIdeas] = useState([]);
  const [timing, setTiming] = useState(null);
  const [demo, setDemo] = useState(null);
  const [pillars, setPillars] = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [selectedReel, setSelectedReel] = useState(null);
  const [reelAnalysis, setReelAnalysis] = useState(null);
  const [analyzingReel, setAnalyzingReel] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggAt, setSuggAt] = useState(null);
  const [genSugg, setGenSugg] = useState(false);
  const [analyzedIds, setAnalyzedIds] = useState([]);
  const [bulk, setBulk] = useState({ running: false, total: 0, done: 0 });
  const pollingRef = useRef(false);
  const [filterPillar, setFilterPillar] = useState("Tutti");
  const [filterFunnel, setFilterFunnel] = useState("Tutti");

  const buildOpts = () => {
    const p = PERIODS.find((x) => x.key === periodKey) || PERIODS[0];
    return p.key === "since" ? { since: startDate } : { months: p.months };
  };

  const loadStatus = useCallback(async () => {
    try {
      const s = await igStatus();
      setStatus(s);
      return s;
    } catch {
      toast.error("Errore nel caricamento stato");
      return null;
    }
  }, []);

  const loadData = useCallback(async (sortKey, opts) => {
    try {
      const [rl, gr, an, tm, dm, sg, ids, bk] = await Promise.all([
        igReels(sortKey, opts), igGrowth(), igGetAnalysis(), igTiming(opts), igDemographics(),
        igGetSuggestions(), igAnalyzedIds(), igBulkStatus(),
      ]);
      setReels(rl || []);
      setGrowth(gr || []);
      setTiming(tm && tm.connected ? tm : null);
      setDemo(dm && dm.connected ? dm : null);
      setSuggestions(sg?.suggerimenti || []);
      setSuggAt(sg?.generated_at || null);
      setAnalyzedIds(ids || []);
      if (bk) setBulk(bk);
      if (an && (an.sintesi || (an.prossimi_temi && an.prossimi_temi.length))) setAnalysis(an);
      igPillars(opts).then((pl) => setPillars(pl && pl.connected ? pl : null)).catch(() => {});
      igPatterns(opts).then((pt) => setPatterns(pt && pt.connected ? pt : null)).catch(() => {});
    } catch {
      /* silenzioso */
    }
  }, []);

  const pollBulk = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    const iv = setInterval(async () => {
      try {
        const st = await igBulkStatus();
        setBulk(st);
        if (!st.running) {
          clearInterval(iv);
          pollingRef.current = false;
          setAnalyzedIds(await igAnalyzedIds());
          if (st.total) toast.success(`Analisi completata: ${st.total} reel`);
        }
      } catch {
        clearInterval(iv);
        pollingRef.current = false;
      }
    }, 3000);
  }, []);

  const loadSavedIdeas = useCallback(async () => {
    try {
      setSavedIdeas(await igGetSavedIdeas() || []);
    } catch {
      /* silenzioso */
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const s = await loadStatus();
      if (s?.connected) {
        let sd = null;
        try {
          sd = (await igSettings()).start_date;
          setStartDate(sd);
        } catch {}
        await loadData("views", { since: sd });
        await loadSavedIdeas();
      }
      setLoading(false);
    })();
  }, [loadStatus, loadData, loadSavedIdeas]);

  useEffect(() => {
    if (bulk.running) pollBulk();
  }, [bulk.running, pollBulk]);

  const onConnect = async () => {
    setConnecting(true);
    try {
      const { auth_url } = await igAuthStart();
      window.location.href = auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile avviare il collegamento");
      setConnecting(false);
    }
  };

  const onSync = async () => {
    setSyncing(true);
    toast.info("Sincronizzazione in corso… può richiedere fino a un minuto.");
    try {
      const res = await igSync();
      toast.success(`Sincronizzati ${res.media} contenuti (${res.reels} reel).`);
      await loadStatus();
      await loadData(sort, buildOpts());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sincronizzazione fallita");
    } finally {
      setSyncing(false);
    }
  };

  const onSort = async (s) => {
    setSort(s);
    try {
      setReels(await igReels(s, buildOpts()));
    } catch {}
  };

  const onPeriod = async (key) => {
    setPeriodKey(key);
    const p = PERIODS.find((x) => x.key === key) || PERIODS[0];
    const opts = p.key === "since" ? { since: startDate } : { months: p.months };
    try {
      const [rl, tm, pl, pt] = await Promise.all([igReels(sort, opts), igTiming(opts), igPillars(opts), igPatterns(opts)]);
      setReels(rl);
      setTiming(tm && tm.connected ? tm : null);
      setPillars(pl && pl.connected ? pl : null);
      setPatterns(pt && pt.connected ? pt : null);
    } catch {}
  };

  const pollAnalysis = () => {
    const started = Date.now();
    const step = async () => {
      let r;
      try {
        r = await igGetAnalysis();
      } catch {
        setAnalyzing(false);
        return;
      }
      if (r && r.running && Date.now() - started < 240000) {
        setTimeout(step, 4000);
        return;
      }
      const hasContent = r && (r.sintesi || (r.prossimi_temi && r.prossimi_temi.length));
      if (hasContent) {
        setAnalysis(r);
        toast.success("Analisi completata");
      } else if (r && r.error) {
        toast.error("Analisi non riuscita, riprova.");
      }
      setAnalyzing(false);
    };
    setTimeout(step, 3000);
  };

  const onAnalyze = async () => {
    setAnalyzing(true);
    try {
      await igAnalyze(buildOpts());
      toast.info("Analisi avviata… ci vuole circa un minuto.");
      pollAnalysis();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analisi non riuscita");
      setAnalyzing(false);
    }
  };

  const onSaveIdea = async (payload) => {
    try {
      await igSaveIdea(payload);
      toast.success("Idea salvata qui in Andamento");
      loadSavedIdeas();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
      return false;
    }
  };

  const onDeleteSavedIdea = async (id) => {
    try {
      await igDeleteSavedIdea(id);
      setSavedIdeas((prev) => prev.filter((x) => x.id !== id));
      toast.success("Idea rimossa");
    } catch {
      toast.error("Errore");
    }
  };

  const onReelClick = async (r) => {
    setSelectedReel(r);
    setReelAnalysis(null);
    try {
      const a = await igGetReelAnalysis(r.media_id);
      if (a && a.hook) setReelAnalysis(a);
    } catch {}
  };

  const onAnalyzeReel = async () => {
    if (!selectedReel) return;
    setAnalyzingReel(true);
    try {
      const a = await igAnalyzeReel(selectedReel.media_id, !!reelAnalysis);
      setReelAnalysis(a);
      toast.success("Analisi completata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analisi non riuscita");
    } finally {
      setAnalyzingReel(false);
    }
  };

  const onReplicateReel = (a, reel) => {
    const idea = {
      categoria: a.content_pillar,
      tipo: a.tipo_funnel,
      hook_fissato: a.hook || "",
      angolo: a.come_replicare || a.topic || "",
      protagonista: "",
      perche_funziona: a.perche_ha_funzionato || "",
      spunto: `Ispirato a un tuo reel andato bene: ${a.topic || (reel.caption || "").slice(0, 60)}`,
    };
    setSelectedReel(null);
    if (onUseIdea) onUseIdea(idea);
    else toast.info("Apri il Generatore per scrivere lo script");
  };

  const onGenerateSuggestions = async () => {
    setGenSugg(true);
    try {
      const d = await igGenerateSuggestions();
      setSuggestions(d.suggerimenti || []);
      setSuggAt(d.generated_at || null);
      toast.success("Suggerimenti pronti");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella generazione");
    } finally {
      setGenSugg(false);
    }
  };

  const onSaveSuggestion = async (s) => {
    try {
      await igSaveIdea({ tema: s.angolo || s.hook, hook: s.hook, angolo: s.angolo, perche: s.perche_funziona });
      loadSavedIdeas();
      toast.success("Idea salvata in Andamento");
      return true;
    } catch {
      toast.error("Errore");
      return false;
    }
  };

  const onUseSuggestion = (s) => {
    const idea = {
      categoria: s.content_pillar,
      tipo: s.tipo_funnel,
      hook_fissato: s.hook || "",
      angolo: s.angolo || "",
      protagonista: "",
      perche_funziona: s.perche_funziona || "",
      spunto: s.quando_pubblicare ? `Consiglio timing: ${s.quando_pubblicare}` : "",
    };
    if (onUseIdea) onUseIdea(idea);
    else toast.info("Apri il Generatore per scrivere lo script");
  };

  const onBulkAnalyze = async () => {
    try {
      const p = PERIODS.find((x) => x.key === periodKey) || PERIODS[0];
      const opts = p.key === "since" ? { since: startDate } : { months: p.months };
      const r = await igBulkAnalyze(opts);
      setBulk({ running: true, total: r.total || 0, done: r.done || 0 });
      toast.info("Analisi avviata sui reel del periodo selezionato (in background)");
      pollBulk();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const periodLabel = (() => {
    const p = PERIODS.find((x) => x.key === periodKey) || PERIODS[0];
    if (p.key === "since") return startDate ? `da inizio lavoro (dal ${new Date(startDate).toLocaleDateString("it-IT")})` : "da inizio lavoro";
    if (p.months === 1) return "l'ultimo mese";
    if (p.months === 0) return "tutto lo storico";
    return `gli ultimi ${p.months} mesi`;
  })();

  const filteredReels = reels.filter((r) => filterMatch(r.pillar, filterPillar) && filterMatch(r.funnel, filterFunnel));

  const onDisconnect = async () => {
    if (!window.confirm("Scollegare l'account Instagram? I dati già scaricati restano salvati.")) return;
    try {
      await igDisconnect();
      toast.success("Account scollegato");
      await loadStatus();
    } catch {
      toast.error("Errore");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-32 text-neutral-500"><Loader2 className="animate-spin" size={28} /></div>;
  }

  if (!status?.connected) {
    return (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <div className="mb-6">
          <h2 className="text-2xl font-display font-extrabold text-white flex items-center gap-2"><Instagram size={24} style={{ color: ACCENT }} /> Andamento Instagram</h2>
          <p className="text-neutral-500 text-sm mt-1">Collega il tuo account per vedere i dati veri dei tuoi reel e far imparare l'app da ciò che funziona.</p>
        </div>
        <ConnectPanel status={status} onConnect={onConnect} connecting={connecting} />
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      {/* Header profilo */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          {status.profile_picture_url ? (
            <img src={status.profile_picture_url} alt="" className="w-14 h-14 rounded-full object-cover border-2" style={{ borderColor: ACCENT }} />
          ) : (
            <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg,#F58529,#DD2A7B,#8134AF)" }}><Instagram size={24} className="text-white" /></div>
          )}
          <div>
            <h2 className="text-xl font-display font-extrabold text-white">@{status.username}</h2>
            <p className="text-xs text-neutral-500">
              {status.last_sync ? `Ultima sincronizzazione: ${new Date(status.last_sync).toLocaleString("it-IT")}` : "Mai sincronizzato — premi Sincronizza"}
            </p>
            <p className="text-[0.7rem] text-neutral-600 mt-0.5" data-testid="ig-auto-sync-note">
              Sync automatico ogni mattina alle 07:00 · analisi AI il lunedì
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="ig-sync-btn"
            onClick={onSync}
            disabled={syncing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50 transition-transform hover:scale-[1.02]"
            style={{ background: ACCENT }}
          >
            {syncing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            Sincronizza
          </button>
          <button data-testid="ig-disconnect-btn" onClick={onDisconnect} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-neutral-500 hover:text-white border border-[#262626] transition-colors" title="Scollega">
            <Unplug size={15} />
          </button>
        </div>
      </div>

      {/* Periodo */}
      <div className="flex items-center gap-3 flex-wrap border border-[#262626] rounded-lg p-3 bg-[#111111]">
        <span className="text-xs uppercase tracking-wide text-neutral-500">Periodo (reel + analisi AI)</span>
        <div className="flex gap-1 flex-wrap items-center">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              data-testid={`ig-period-${p.key}`}
              onClick={() => onPeriod(p.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${periodKey === p.key ? "text-white" : "text-neutral-500 hover:text-neutral-300 border border-[#262626]"}`}
              style={periodKey === p.key ? { background: ACCENT } : {}}
            >
              {p.label}
            </button>
          ))}
          {periodKey === "since" && startDate && (
            <span className="text-xs text-neutral-600 ml-1">dal {new Date(startDate).toLocaleDateString("it-IT")} → cresce nel tempo</span>
          )}
        </div>
      </div>

      {/* Stat */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Users} label="Follower" value={fmtNum(status.followers_count)} />
        <Stat icon={Instagram} label="Contenuti" value={fmtNum(status.media_count)} />
        <Stat icon={Eye} label="Views totali (reel)" value={fmtNum(reels.reduce((a, r) => a + (r.views || 0), 0))} color="#16A34A" />
        <Stat icon={Bookmark} label="Salvataggi totali" value={fmtNum(reels.reduce((a, r) => a + (r.saved || 0), 0))} color="#D97706" />
      </div>

      <GrowthChart data={growth} />

      <TimingPanel timing={timing} />

      <DemographicsPanel demo={demo} />

      <PillarComparePanel data={pillars} />

      <PatternsPanel data={patterns} />

      <SuggestionsPanel
        suggestions={suggestions}
        generatedAt={suggAt}
        generating={genSugg}
        onGenerate={onGenerateSuggestions}
        onUse={onUseSuggestion}
        onSave={onSaveSuggestion}
      />

      <AiPanel analysis={analysis} analyzing={analyzing} onAnalyze={onAnalyze} periodLabel={periodLabel} onSaveIdea={onSaveIdea} />

      <SavedIdeasPanel ideas={savedIdeas} onDelete={onDeleteSavedIdea} onUseIdea={onUseIdea} />

      {/* Reel */}
      <div>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
          <h3 className="font-display font-bold text-white flex items-center gap-2"><BarChart3 size={18} style={{ color: ACCENT }} /> I tuoi reel {reels.length ? `(${filteredReels.length}${filteredReels.length !== reels.length ? ` / ${reels.length}` : ""})` : ""}</h3>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              data-testid="ig-bulk-analyze-btn"
              onClick={onBulkAnalyze}
              disabled={bulk.running}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold text-white disabled:opacity-60"
              style={{ background: "#D97706" }}
              title="Analizza con l'AI tutti i reel non ancora analizzati"
            >
              {bulk.running ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
              {bulk.running ? `Analisi… ${bulk.done}/${bulk.total || "?"}` : "Analizza tutti con AI"}
            </button>
            <div className="flex gap-1 flex-wrap">
              {SORTS.map((s) => (
                <button
                  key={s.id}
                  data-testid={`ig-sort-${s.id}`}
                  onClick={() => onSort(s.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${sort === s.id ? "text-white" : "text-neutral-500 hover:text-neutral-300 border border-[#262626]"}`}
                  style={sort === s.id ? { background: ACCENT } : {}}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-3">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[0.65rem] uppercase tracking-wide text-neutral-600 mr-0.5">Pillar</span>
            {PILLARS.map((p) => <FilterChip key={p} label={p} active={filterPillar === p} onClick={() => setFilterPillar(p)} />)}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[0.65rem] uppercase tracking-wide text-neutral-600 mr-0.5">Funnel</span>
            {FUNNELS.map((f) => <FilterChip key={f} label={f} active={filterFunnel === f} onClick={() => setFilterFunnel(f)} />)}
          </div>
        </div>
        <p className="text-[0.7rem] text-neutral-600 mb-3">
          {analyzedIds.length} reel già analizzati dall'AI · clicca un reel per vederne l'analisi
          {(filterPillar !== "Tutti" || filterFunnel !== "Tutti") && " · i filtri mostrano solo i reel analizzati"}
        </p>
        {bulk.running && (
          <div className="mb-4 h-1.5 rounded-full bg-[#1f1f1f] overflow-hidden" data-testid="ig-bulk-progress">
            <div className="h-full transition-all duration-500" style={{ width: `${bulk.total ? Math.round((bulk.done / bulk.total) * 100) : 5}%`, background: "#D97706" }} />
          </div>
        )}

        {(() => {
          const filtered = filteredReels;
          if (reels.length === 0) {
            return (
              <div className="border border-dashed border-[#262626] rounded-xl p-10 text-center text-neutral-500">
                <Instagram size={32} className="mx-auto mb-3 text-neutral-700" />
                Nessun reel ancora. Premi <b className="text-white">Sincronizza</b> per scaricare i tuoi contenuti.
              </div>
            );
          }
          if (filtered.length === 0) {
            return (
              <div className="border border-dashed border-[#262626] rounded-xl p-10 text-center text-neutral-500" data-testid="ig-reels-empty-filter">
                Nessun reel con questi filtri. Prova a cambiare Pillar/Funnel o analizza più reel con l'AI.
              </div>
            );
          }
          const aset = new Set(analyzedIds);
          return (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filtered.map((r, i) => <ReelCard key={r.media_id} r={r} rank={i + 1} onClick={() => onReelClick(r)} analyzed={aset.has(r.media_id)} />)}
            </div>
          );
        })()}
      </div>

      <ReelDetail
        reel={selectedReel}
        open={!!selectedReel}
        onClose={() => setSelectedReel(null)}
        analysis={reelAnalysis}
        analyzing={analyzingReel}
        onAnalyze={onAnalyzeReel}
        onReplicate={onReplicateReel}
      />
    </motion.div>
  );
}
